import { failure } from '../domain/nutrition.ts';
import type { ShoppingStatus } from '../domain/nutrition.ts';
import type { NutritionRepository, ShoppingItemView } from '../ports/nutrition-repository.ts';
import { nutritionIntent } from './nutrition-intent.ts';

export interface ShoppingFields {
  name: string;
  quantity?: number;
  unit?: string;
  note?: string;
}

function valid(fields: ShoppingFields) {
  return !!fields.name.trim()
    && (fields.quantity === undefined || (
      Number.isFinite(fields.quantity)
      && fields.quantity >= 0
    ))
    && (fields.unit === undefined || !!fields.unit.trim())
    && (fields.note === undefined || !!fields.note.trim());
}

export class NutritionShopping {
  constructor(
    private repo: Pick<NutritionRepository,
      'readContext' | 'listShoppingItems' | 'readShoppingItemRevision' | 'createShoppingItem' | 'editShoppingItem'>,
    private id: () => string,
  ) {}

  listShoppingItems() { return this.repo.listShoppingItems(); }
  getShoppingItemRevision(id: string, revision: number) { return this.repo.readShoppingItemRevision(id, revision); }

  createShoppingItem(fields: ShoppingFields) {
    const draft = structuredClone(fields);
    return nutritionIntent(async () => {
      if (!valid(draft)) return failure('VALIDATION_ERROR');
      const context = await this.repo.readContext();
      if (!context.ok) return context;
      const shoppingItemId = this.id();
      return { ok: true as const, value: {
        context: context.value,
        operationId: this.id(),
        item: {
          shoppingItemId,
          revision: 1,
          name: draft.name.trim(),
          status: 'PENDIENTE' as const,
          ...(draft.quantity !== undefined
            ? { quantity: draft.quantity } : {}),
          ...(draft.unit?.trim() ? { unit: draft.unit.trim() } : {}),
          ...(draft.note?.trim() ? { note: draft.note.trim() } : {}),
        },
      } };
    }, command => this.repo.createShoppingItem(command), async command => {
      const all = await this.repo.listShoppingItems();
      if (!all.ok) return all;
      const found = all.value.find(view => view.item.shoppingItemId === command.item.shoppingItemId);
      return found ? { ok: true as const, value: found } : failure('NOT_FOUND');
    });
  }

  editShoppingItem(
    view: ShoppingItemView,
    fields: ShoppingFields,
    status: ShoppingStatus = view.item.status,
  ) {
    const observed = structuredClone(view);
    const draft = structuredClone(fields);
    return nutritionIntent(async () => {
      if (!valid(draft)) return failure('VALIDATION_ERROR');
      return { ok: true as const, value: {
        context: observed.context,
        operationId: this.id(),
        shoppingItemId: observed.item.shoppingItemId,
        expectedRevision: observed.item.revision,
        item: {
          shoppingItemId: observed.item.shoppingItemId,
          revision: observed.item.revision + 1,
          name: draft.name.trim(),
          status,
          ...(draft.quantity !== undefined
            ? { quantity: draft.quantity } : {}),
          ...(draft.unit?.trim() ? { unit: draft.unit.trim() } : {}),
          ...(draft.note?.trim() ? { note: draft.note.trim() } : {}),
        },
      } };
    }, command => this.repo.editShoppingItem(command), async command => {
      const all = await this.repo.listShoppingItems();
      if (!all.ok) return all;
      const found = all.value.find(item => item.item.shoppingItemId === command.shoppingItemId);
      return found ? { ok: true as const, value: found } : failure('NOT_FOUND');
    });
  }
}
